import {
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  fetchSignInMethodsForEmail,
  UserCredential,
  GoogleAuthProvider,
  signInWithCredential,
  linkWithCredential,
} from 'firebase/auth';
import i18next from 'i18next';
import { AuthErrorCodes } from 'firebase/auth';
import { captureException } from '@sentry/browser';

import '@pages/app';
import '@components/passwordLogin';
import { showBottomSheet } from '@components/bottomSheet';
import { globalSpinnerController } from '@/components/Spinner';
import {
  appleAuthProvider,
  auth,
  authProvidersMap,
  getIsNewUser,
  idTokenHandler,
  isCalledFromParentSite,
  linkAccountWithPopup,
} from '@services/authService';
import {
  isMobile,
  isAppleDeviceWebApp,
  isKakaoOrNaver,
  isSamsungInternet,
  isIOS,
} from '@utils/uaParser';
import {
  sharedData,
  setCredentialForLinkAccount,
  clearCredentialForLinkAccount,
  clearSocialSignInError,
  socialSignInError as socialSignInErrorState,
  setSocialSignInError,
  loadAppTypeFromStorage,
  type AppType,
} from '@shares/dataManager';
import {
  isAnchorElement,
  isDialogElement,
  isHTMLElement,
} from '@utils/elementTypeGuards';
import { provider, signInMethodType } from '@/types/auth';
import { FirebaseError } from 'firebase/app';
import { PAGE_PATHS, loginProblemMailScheme } from '@constants/url';
import {
  IOSLoginWebkitMessageHandler,
  postTokensToIOSApp,
} from '@/utils/IOSwebkitMessageHandler';
import { ACTION_TYPES } from '@constants/actions';
import saveProviderType from '@utils/saveProviderType';
import getIsNeedOnboarding from '@/utils/getIsNeedOnboarding';
import {
  getKakaoCredential,
  tryKakaoLogin,
  tryLinkKakaoAccount,
} from '@/services/kakaoService';
import { moveToEmailLinkAccountPage } from '@/services/linkAccountService';

let socialSignInError: FirebaseError | null = null;
let linkedMethod: signInMethodType | undefined = undefined;
let isSamsungBrowserGooglLoginWarningBottomSheetShown = false;

const BLOCKED_LOGIN_PROVIDERS_IN_EMBEDDED_BROWSER: provider[] = ['google.com'];

const SocialLoginContainer = document.getElementById(
  'social-login-container',
) as HTMLElement;

const googleLoginText = SocialLoginContainer.querySelector('#google-login');
const facebookLoginText = SocialLoginContainer.querySelector('#facebook-login');
const appleLoginText = SocialLoginContainer.querySelector('#apple-login');
const kakaoLoginText = SocialLoginContainer.querySelector('#kakao-login');
const emailLoginText = SocialLoginContainer.querySelector('#email-login');
const emailSignUpText = SocialLoginContainer.querySelector('#sign-up');
const problemWithSignIn = SocialLoginContainer.querySelector(
  '#problem-with-signIn',
);
const googleLoginEl = SocialLoginContainer.querySelector('#google-login-btn');
const facebookLoginEl = SocialLoginContainer.querySelector(
  '#facebook-login-btn',
);
const appleLoginEl = SocialLoginContainer.querySelector('#apple-login-btn');
const kakaoLoginEl = SocialLoginContainer.querySelector('#kakao-login-btn');
const emailLoginEl = SocialLoginContainer.querySelector('#email-login-btn');
const emailSignUpEl = SocialLoginContainer.querySelector('#sign-up-btn');
const logoEl = SocialLoginContainer.querySelector<HTMLImageElement>('#logo');
const welcomeTitleEl =
  SocialLoginContainer.querySelector<HTMLElement>('#welcome-title');
const welcomeSubtitleEl =
  SocialLoginContainer.querySelector<HTMLElement>('#welcome-subtitle');

const LOGO_BY_APP_TYPE: Record<AppType, string> = {
  pickleball_studio: '/img/pickleball_studio_logo.webp',
  studio: '/img/studio_logo.webp',
  live: '/img/live_logo.webp',
};

if (logoEl) {
  const appTypeFromStorage = loadAppTypeFromStorage();
  if (appTypeFromStorage === null) {
    logoEl.style.aspectRatio = 'auto';
  }
  if (appTypeFromStorage) {
    logoEl.src = LOGO_BY_APP_TYPE[appTypeFromStorage];
  }

  //* 이미지 결정/로드가 끝난 시점에만 보이도록 (initial src로 인한 깜빡임 방지)
  const revealLogo = () => {
    logoEl.style.visibility = 'visible';
  };

  if (logoEl.complete && logoEl.naturalHeight > 0) {
    revealLogo();
  } else {
    logoEl.addEventListener('load', revealLogo, { once: true });
    //* 로드 실패해도 alt 텍스트는 노출되도록 표시
    logoEl.addEventListener('error', revealLogo, { once: true });
  }
}

const dialog = document.querySelector('dialog');
//TODO iOS 페이스북 로그인이 문제 없을시 페이스북 로그인 제한 다이얼로그 제거
const blockFacebookLoginDialog = document.querySelector<HTMLDialogElement>(
  '.block-facebook-login-dialog',
);
const iosAppUpdateDialog = document.querySelector<HTMLDialogElement>(
  '.ios-app-update-dialog',
);
const linkAccountErrorDialog = document.querySelector<HTMLDialogElement>(
  '.link-account-error-dialog',
);

const dialogOk = dialog?.querySelector('.ok');
const dialogCancel = dialog?.querySelector('.cancel');
const dialogTitle = dialog?.querySelector('#dialog_title');
const dialogDesc1 = dialog?.querySelector('#desc1');
const ballonList = document.querySelectorAll('.ballon');

const blockFacebookDialogOk = blockFacebookLoginDialog?.querySelector('.ok');
const blockFacebookDialogTitle =
  blockFacebookLoginDialog?.querySelector('#dialog_title');
const blockFacebookDialogDesc1 =
  blockFacebookLoginDialog?.querySelector('#desc1');

const iosAppUpdateDialogOk = iosAppUpdateDialog?.querySelector('.ok');
const iosAppUpdateDialogTitle =
  iosAppUpdateDialog?.querySelector('#dialog_title');
const iosAppUpdateDialogDesc1 = iosAppUpdateDialog?.querySelector('#desc1');

const linkAccountErrorDialogOk = linkAccountErrorDialog?.querySelector('.ok');
const linkAccountErrorDialogTitle =
  linkAccountErrorDialog?.querySelector('#dialog_title');
const linkAccountErrorDialogDesc1 =
  linkAccountErrorDialog?.querySelector('#desc1');

try {
  if (
    !isHTMLElement(googleLoginText) ||
    !isHTMLElement(facebookLoginText) ||
    !isHTMLElement(appleLoginText) ||
    !isHTMLElement(kakaoLoginText) ||
    !isHTMLElement(emailLoginText) ||
    !isHTMLElement(emailSignUpText) ||
    !isHTMLElement(googleLoginEl) ||
    !isHTMLElement(facebookLoginEl) ||
    !isHTMLElement(appleLoginEl) ||
    !isHTMLElement(kakaoLoginEl) ||
    !isHTMLElement(emailLoginEl) ||
    !isHTMLElement(emailSignUpEl) ||
    !isDialogElement(dialog) ||
    !isDialogElement(blockFacebookLoginDialog) ||
    !isDialogElement(iosAppUpdateDialog) ||
    !isHTMLElement(dialogOk) ||
    !isHTMLElement(dialogCancel) ||
    !isHTMLElement(dialogTitle) ||
    !isHTMLElement(dialogDesc1) ||
    !isHTMLElement(blockFacebookDialogOk) ||
    !isHTMLElement(blockFacebookDialogTitle) ||
    !isHTMLElement(blockFacebookDialogDesc1) ||
    !isHTMLElement(iosAppUpdateDialogOk) ||
    !isHTMLElement(iosAppUpdateDialogTitle) ||
    !isHTMLElement(iosAppUpdateDialogDesc1) ||
    !isDialogElement(linkAccountErrorDialog) ||
    !isHTMLElement(linkAccountErrorDialogOk) ||
    !isHTMLElement(linkAccountErrorDialogTitle) ||
    !isHTMLElement(linkAccountErrorDialogDesc1) ||
    !isAnchorElement(problemWithSignIn) ||
    !isHTMLElement(welcomeTitleEl) ||
    !isHTMLElement(welcomeSubtitleEl) ||
    !Array.from(ballonList).every((ballon) => isHTMLElement(ballon))
  ) {
    throw new Error('Element not found');
  }

  welcomeTitleEl.textContent = i18next.t('index.welcome.title');
  welcomeSubtitleEl.textContent = i18next.t('index.welcome.subtitle');

  googleLoginText.textContent = i18next.t('googleSignIn');
  facebookLoginText.textContent = i18next.t('facebookLogin');
  appleLoginText.textContent = i18next.t('appleLogin');
  kakaoLoginText.textContent = i18next.t('kakaoLogin');
  emailLoginText.textContent = i18next.t('signIn');
  emailSignUpText.textContent = i18next.t('signUpButtonLabel');
  problemWithSignIn.textContent = i18next.t('problemWithSignIn');
  ballonList.forEach((ballon) => {
    ballon.textContent = i18next.t('recentLogin');
  });

  dialogTitle.textContent = i18next.t('index.dialog.title');
  dialogDesc1.textContent = i18next.t('index.dialog.desc1');
  dialogOk.textContent = i18next.t('index.dialog.linkAccount');
  dialogCancel.textContent = i18next.t('index.dialog.cancel');

  blockFacebookDialogTitle.textContent = i18next.t(
    'index.blockFacebookLoginDialog.title',
  );
  blockFacebookDialogDesc1.textContent = i18next.t(
    'index.blockFacebookLoginDialog.desc1',
  );
  blockFacebookDialogOk.textContent = i18next.t(
    'index.blockFacebookLoginDialog.ok',
  );

  iosAppUpdateDialogTitle.textContent = i18next.t(
    'index.iosAppUpdateDialog.title',
  );
  iosAppUpdateDialogDesc1.textContent = i18next.t(
    'index.iosAppUpdateDialog.desc1',
  );
  iosAppUpdateDialogOk.textContent = i18next.t('index.iosAppUpdateDialog.ok');

  linkAccountErrorDialogTitle.textContent = i18next.t(
    'index.linkAccountErrorDialog.title',
  );
  linkAccountErrorDialogOk.textContent = i18next.t(
    'index.linkAccountErrorDialog.ok',
  );

  type LinkAccountErrorReason = 'emailMismatch' | 'alreadyInUse' | 'generic';
  const showLinkAccountErrorDialog = (reason: LinkAccountErrorReason) => {
    linkAccountErrorDialogDesc1.textContent = i18next.t(
      `index.linkAccountErrorDialog.${reason}`,
    );
    linkAccountErrorDialog.showModal();
  };

  //* 연동 흐름 전체에서 공통으로 쓰는 에러 분류기.
  //* 사용자 취소(팝업 닫기 등)는 정상 흐름이므로 조용히 종료한다.
  const handleLinkAccountError = (error: unknown) => {
    if (
      error instanceof FirebaseError &&
      (error.code === 'auth/popup-closed-by-user' ||
        error.code === 'auth/cancelled-popup-request' ||
        error.code === 'auth/user-cancelled')
    ) {
      return;
    }

    captureException(error);

    if (
      error instanceof FirebaseError &&
      error.code === 'auth/credential-already-in-use'
    ) {
      showLinkAccountErrorDialog('alreadyInUse');
      return;
    }

    showLinkAccountErrorDialog('generic');
  };

  linkAccountErrorDialogOk.addEventListener('click', () =>
    linkAccountErrorDialog.close(),
  );

  const currentLanguage = i18next.language;

  const { callbackUrl, platform } = sharedData.queries;

  if (currentLanguage === 'ko') {
    problemWithSignIn.href = loginProblemMailScheme.ko;
  } else problemWithSignIn.href = loginProblemMailScheme.en;

  // URL에서 인가 코드(code) 확인 로직 추가
  const urlParams = new URLSearchParams(window.location.search);
  const kakaoAuthCode = urlParams.get('code');
  const kakaoState = urlParams.get('state');

  const loginWithKakaoAuthCode = async (
    kakaoAuthCode: string,
    kakaoState: string | null,
  ) => {
    try {
      globalSpinnerController.showSpinner();
      const kakaoCredential = await getKakaoCredential(kakaoAuthCode);

      if (kakaoCredential === undefined) {
        throw new Error('Failed to get Kakao credential');
      }

      const kakaoUserCredential = await signInWithCredential(
        auth,
        kakaoCredential,
      );

      if (kakaoState === 'link_account' && socialSignInErrorState) {
        const credentialForLinkAccount = OAuthProvider.credentialFromError(
          socialSignInErrorState,
        );

        if (credentialForLinkAccount === null) {
          captureException(
            new Error('Failed to extract credential for link account'),
          );
          globalSpinnerController.hideSpinner();
          showLinkAccountErrorDialog('generic');
          return;
        }

        const linkCredential = await linkWithCredential(
          kakaoUserCredential.user,
          credentialForLinkAccount,
        );

        const idToken = await linkCredential.user.getIdToken(true);

        const { customToken } = await idTokenHandler({
          idToken,
          actionType: ACTION_TYPES.SOCIAL_SIGNIN,
        });

        await redirectToUpdatedCallbackUrl({
          idToken,
          customToken,
          isNewUser: false,
          linkedMethod: credentialForLinkAccount.providerId as signInMethodType,
          uid: linkCredential.user.uid,
        });

        clearCredentialForLinkAccount();
        clearSocialSignInError();
        return;
      }

      await handleCredential(kakaoUserCredential);
      saveProviderType(kakaoUserCredential.providerId);
    } catch (error) {
      globalSpinnerController.hideSpinner();

      if (
        error instanceof FirebaseError &&
        error.code === AuthErrorCodes.NEED_CONFIRMATION
      ) {
        handleNeedConfirmationError(error);
        return;
      }

      //* Kakao 연동 흐름에서 발생한 에러는 사용자에게 다이얼로그로 알림
      if (kakaoState === 'link_account') {
        handleLinkAccountError(error);
        return;
      }

      captureException(error);
    }
  };

  const handleFacebookRedirectResult = async () => {
    //* 사용자가 Facebook 연동 redirect로부터 돌아온 것인지 catch 블록에서도 알 수 있도록
    //* 함수 시작 시점에 flag를 미리 읽어 둔다
    const wasLinkRedirect =
      sessionStorage.getItem('facebookLinkRedirect') === 'true';
    sessionStorage.removeItem('facebookLinkRedirect');

    try {
      globalSpinnerController.showSpinner();
      const result = await getRedirectResult(auth);
      if (!result) return;

      if (wasLinkRedirect && socialSignInErrorState) {
        const pendingCred = OAuthProvider.credentialFromError(
          socialSignInErrorState,
        );
        if (pendingCred === null) {
          captureException(
            new Error('No pending credential after Facebook link redirect'),
          );
          showLinkAccountErrorDialog('generic');
          return;
        }

        const linkCredential = await linkWithCredential(
          result.user,
          pendingCred,
        );
        await handleCredential(linkCredential);
        clearCredentialForLinkAccount();
        clearSocialSignInError();
        return;
      }

      await handleCredential(result);
      saveProviderType(result.providerId);
    } catch (error) {
      if (
        error instanceof FirebaseError &&
        error.code === AuthErrorCodes.NEED_CONFIRMATION
      ) {
        handleNeedConfirmationError(error);
        return;
      }

      //* 연동 redirect로 진입한 경우엔 일반 로그인 에러 핸들러 대신 연동용 다이얼로그로 알림
      if (wasLinkRedirect) {
        handleLinkAccountError(error);
        return;
      }

      captureException(error);
      handleError(error);
    } finally {
      globalSpinnerController.hideSpinner();
    }
  };

  if (kakaoAuthCode) {
    loginWithKakaoAuthCode(kakaoAuthCode, kakaoState);
  } else {
    handleFacebookRedirectResult();
  }

  const parseTokenDict = (authCredential: string) => {
    if (!authCredential || typeof authCredential !== 'string') {
      return { idToken: null, accessToken: null };
    }

    const extract = (key: string) => {
      const regex = new RegExp(
        `"${key}":\\s*(?:Optional\\("([^"]+)"\\)|"([^"]+)")`,
      );
      const match = authCredential.match(regex);
      return match ? (match[1] ?? match[2]) : null;
    };

    const idToken = extract('idToken');
    const accessToken = extract('accessToken');

    return { idToken, accessToken };
  };

  // * IOS 앱에서 [Google, Apple] 로그인 시 호출하는 함수
  window.handleAuthCredentialForSSO = async (
    authCredential: string,
    providerType: string,
    isLink: string,
  ) => {
    const isLinkingSocialAccount = isLink === 'true';

    globalSpinnerController.showSpinner();

    if (providerType === 'google') {
      const { idToken, accessToken } = parseTokenDict(authCredential);

      const credential = GoogleAuthProvider.credential(idToken, accessToken);

      try {
        const googleUserCredential = await signInWithCredential(
          auth,
          credential,
        );

        //* 일반 로그인인 경우
        if (isLinkingSocialAccount === false) {
          await handleCredential(googleUserCredential);
          saveProviderType(googleUserCredential.providerId);
          return;
        }

        //* 계정 연결 상황인 경우
        const credentialForLinkAccount = socialSignInError
          ? OAuthProvider.credentialFromError(socialSignInError)
          : null;

        if (credentialForLinkAccount === null) {
          captureException(
            new Error('No pending credential for iOS Google SSO link'),
          );
          globalSpinnerController.hideSpinner();
          showLinkAccountErrorDialog('generic');
          return;
        }

        const linkCredential = await linkWithCredential(
          googleUserCredential.user,
          credentialForLinkAccount,
        );

        await handleCredential(linkCredential);
        saveProviderType(googleUserCredential.providerId);
        clearCredentialForLinkAccount();
        clearSocialSignInError();
      } catch (error) {
        globalSpinnerController.hideSpinner();

        if (
          error instanceof FirebaseError &&
          error.code === AuthErrorCodes.NEED_CONFIRMATION
        ) {
          handleNeedConfirmationError(error);
          return;
        }

        if (isLinkingSocialAccount) {
          handleLinkAccountError(error);
          return;
        }

        captureException(error);
        showUpdateIosAppUpdateDialog();
      }

      return;
    }

    if (providerType === 'apple') {
      //* idToken 추출
      const idTokenMatch = authCredential.match(
        /idToken": Optional\("([^"]+)"\)/,
      );
      const idToken = idTokenMatch ? idTokenMatch[1] : null;

      //* rawNonce 추출
      const rawNonceMatch = authCredential.match(
        /rawNonce": Optional\("([^"]+)"\)/,
      );
      const rawNonce = rawNonceMatch ? rawNonceMatch[1] : null;

      const credential = appleAuthProvider.credential({
        idToken: idToken ?? undefined,
        rawNonce: rawNonce ?? undefined,
      });

      try {
        const appleUserCredential = await signInWithCredential(
          auth,
          credential,
        );

        //* 일반 로그인인 경우
        if (isLinkingSocialAccount === false) {
          await handleCredential(appleUserCredential);
          saveProviderType(appleUserCredential.providerId);
          return;
        }

        //* 계정 연결 상황인 경우
        const credentialForLinkAccount = socialSignInError
          ? OAuthProvider.credentialFromError(socialSignInError)
          : null;

        if (credentialForLinkAccount === null) {
          captureException(
            new Error('No pending credential for iOS Apple SSO link'),
          );
          globalSpinnerController.hideSpinner();
          showLinkAccountErrorDialog('generic');
          return;
        }

        const linkCredential = await linkWithCredential(
          appleUserCredential.user,
          credentialForLinkAccount,
        );

        await handleCredential(linkCredential);
        saveProviderType(appleUserCredential.providerId);
        clearCredentialForLinkAccount();
        clearSocialSignInError();
      } catch (error) {
        globalSpinnerController.hideSpinner();

        if (
          error instanceof FirebaseError &&
          error.code === AuthErrorCodes.NEED_CONFIRMATION
        ) {
          handleNeedConfirmationError(error);
          return;
        }

        if (isLinkingSocialAccount) {
          handleLinkAccountError(error);
          return;
        }

        captureException(error);
      }
    }
  };

  const handleNeedConfirmationError = (error: FirebaseError) => {
    globalSpinnerController.hideSpinner();
    setSocialSignInError(error);
    socialSignInError = error;
    dialog.showModal();
  };

  const showUpdateIosAppUpdateDialog = () => {
    iosAppUpdateDialog.showModal();
  };

  const handleEmbeddedBrowserLogin = () => {
    showBottomSheet('embeddedBrowser');
  };

  const webAppLogin = (providerType: provider) => {
    IOSLoginWebkitMessageHandler(providerType);
  };

  const handleCredential = async (credential: UserCredential) => {
    const idToken = await credential.user.getIdToken(true);
    const isNewUser = getIsNewUser(credential);
    const actionType = isNewUser
      ? ACTION_TYPES.SOCIAL_SIGNUP
      : ACTION_TYPES.SOCIAL_SIGNIN;

    const { customToken } = await idTokenHandler({
      idToken,
      actionType,
      isNewUser,
    });

    if (isAppleDeviceWebApp) {
      await postTokensToIOSApp({
        idToken,
        customToken,
      });
    }

    if (isCalledFromParentSite) {
      window.opener.postMessage(
        {
          result: 'success',
          data: customToken,
        },
        '*',
      );
    } else {
      await redirectToUpdatedCallbackUrl({
        idToken,
        customToken,
        isNewUser,
        uid: credential.user.uid,
      });
    }

    // 로그인 성공 시 credential 및 error 삭제
    clearCredentialForLinkAccount();
    clearSocialSignInError();
  };

  const handleDefaultLogin = async (providerType: provider) => {
    try {
      const provider = authProvidersMap[providerType];

      const userCredential = await signInWithPopup(auth, provider);

      await handleCredential(userCredential);
      saveProviderType(userCredential.providerId);
    } catch (error) {
      if (error instanceof FirebaseError) {
        if (error.code === AuthErrorCodes.NEED_CONFIRMATION) {
          handleNeedConfirmationError(error);
        } else {
          captureException(error);
          throw error; // Re-throw to handle it in the outer catch
        }
      } else {
        captureException(error);
        throw error; // Re-throw to handle it in the outer catch
      }
    }
  };

  const handleError = (err: unknown) => {
    if (
      err instanceof FirebaseError &&
      err.code === AuthErrorCodes.NEED_CONFIRMATION
    ) {
      handleNeedConfirmationError(err);
    }
  };

  const socialLoginHandler = async (providerType: provider) => {
    linkedMethod = providerType;
    // 카카오, 네이버앱에서 접속하는 경우 로그인 불가하기 때문에 대체 방안 제공하는 바텀시트 띄움
    if (
      BLOCKED_LOGIN_PROVIDERS_IN_EMBEDDED_BROWSER.includes(providerType) &&
      isMobile &&
      isKakaoOrNaver
    ) {
      handleEmbeddedBrowserLogin();
      return;
    }

    if (
      isSamsungInternet &&
      providerType === 'google.com' &&
      !isSamsungBrowserGooglLoginWarningBottomSheetShown
    ) {
      showBottomSheet('samsungBrowser');
      isSamsungBrowserGooglLoginWarningBottomSheetShown = true;
      return;
    }

    //* iOS WebView에서 Facebook 로그인은 popup 차단 이슈로 signInWithRedirect 방식 사용
    if (isAppleDeviceWebApp && isIOS && providerType === 'facebook.com') {
      try {
        globalSpinnerController.showSpinner();
        const provider = authProvidersMap[providerType];
        await signInWithRedirect(auth, provider);
        return;
      } catch (err) {
        captureException(err);
        handleError(err);
        globalSpinnerController.hideSpinner();
        return;
      }
    }

    //* 스튜디오 IOS 앱에서 쇼셜 로그인을 요청하는 경우 네이티브 코드 호출하여 authCredential 요청
    //* IOS 앱에서 handleAuthCredentialForSSO 호출함
    //* 단, facebook 로그인은 지원하지 않기 떄문에 예외처리
    if (isAppleDeviceWebApp && providerType !== 'facebook.com') {
      // if (providerType === 'facebook.com' ) {
      // blockFacebookLogin();
      // return;
      // }

      webAppLogin(providerType);
      return;
    }

    // 이외 모든 로그인 시도는 기본 웹 로그인 방식으로 처리
    try {
      globalSpinnerController.showSpinner();
      await handleDefaultLogin(providerType);
    } catch (err) {
      captureException(err);
      handleError(err);
      globalSpinnerController.hideSpinner();
    }
  };

  const emailLoginClickHandler = async () => {
    location.href = 'login' + window.location.search;
  };

  const emailSignUpClickHandler = () => {
    location.href = `${PAGE_PATHS.REGISTER}` + window.location.search;
  };

  const dialogOkClickHandler = async (
    socialSignInError: FirebaseError | null,
  ) => {
    if (socialSignInError === null) return;

    try {
      dialog.close();
      globalSpinnerController.showSpinner();

      const credentialForLinkAccount =
        OAuthProvider.credentialFromError(socialSignInError);

      setCredentialForLinkAccount(credentialForLinkAccount);

      //TODO: 확인 필요
      const email = (socialSignInError?.customData?.email || '') as string;

      const signInMethods = await fetchSignInMethodsForEmail(auth, email);

      const signInMethod = signInMethods[0];

      if (signInMethod === 'password') {
        moveToEmailLinkAccountPage('web');
        return;
      }

      if (credentialForLinkAccount === null) {
        throw new Error('No credential available for linking the account.');
      }

      //* iOS WebView에서 Facebook은 popup 차단으로 인해 연동도 signInWithRedirect 방식 사용
      //* redirect 복귀 시 handleFacebookRedirectResult가 linkWithCredential까지 마무리
      if (isAppleDeviceWebApp && isIOS && signInMethod === 'facebook.com') {
        sessionStorage.setItem('facebookLinkRedirect', 'true');
        await signInWithRedirect(auth, authProvidersMap['facebook.com']);
        return;
      }

      if (isIOS) {
        IOSLoginWebkitMessageHandler(signInMethod as provider, true);
        return;
      }

      if (signInMethod === 'oidc.kakao') {
        tryLinkKakaoAccount();
        return;
      }

      const userCredential = await linkAccountWithPopup(
        credentialForLinkAccount,
        signInMethod as provider,
        email,
      );

      //* 팝업에서 연동 대상이 아닌 다른 계정으로 로그인하면 null 반환
      if (userCredential === null) {
        globalSpinnerController.hideSpinner();
        showLinkAccountErrorDialog('emailMismatch');
        return;
      }

      const idToken = await userCredential.user.getIdToken(true);
      const isNewUser = getIsNewUser(userCredential);
      const uid = userCredential.user.uid;

      const { customToken } = await idTokenHandler({
        idToken,
        actionType: ACTION_TYPES.SOCIAL_SIGNIN,
      });

      if (isCalledFromParentSite) {
        window.opener.postMessage(
          {
            result: 'success',
            data: customToken,
          },
          '*',
        );
      } else {
        await redirectToUpdatedCallbackUrl({
          idToken,
          customToken,
          isNewUser,
          linkedMethod,
          uid,
        });
      }

      // 계정 연동 성공 시 credential 및 error 삭제
      clearCredentialForLinkAccount();
      clearSocialSignInError();
    } catch (error) {
      globalSpinnerController.hideSpinner();
      handleLinkAccountError(error);
    }
  };

  const dialogCancelBtnClickHandler = () => {
    dialog.close();
  };

  googleLoginEl.addEventListener('click', () =>
    socialLoginHandler('google.com'),
  );
  facebookLoginEl.addEventListener('click', () =>
    socialLoginHandler('facebook.com'),
  );
  appleLoginEl.addEventListener('click', () => socialLoginHandler('apple.com'));
  kakaoLoginEl.addEventListener('click', tryKakaoLogin);
  emailLoginEl.addEventListener('click', emailLoginClickHandler);
  emailSignUpEl.addEventListener('click', emailSignUpClickHandler);
  dialogOk.addEventListener('click', () =>
    dialogOkClickHandler(socialSignInError),
  );
  dialogCancel.addEventListener('click', dialogCancelBtnClickHandler);
  blockFacebookDialogOk.addEventListener('click', () =>
    blockFacebookLoginDialog.close(),
  );
  iosAppUpdateDialogOk.addEventListener('click', () =>
    iosAppUpdateDialog.close(),
  );

  const redirectToUpdatedCallbackUrl = async ({
    idToken,
    customToken,
    isNewUser,
    linkedMethod,
    uid,
  }: {
    idToken: string;
    customToken: string;
    isNewUser: boolean;
    linkedMethod?: signInMethodType;
    uid: string;
  }) => {
    try {
      const kakaoAuthContext = sessionStorage.getItem('kakao_auth_context');

      if (kakaoAuthContext !== null) {
        const {
          callbackUrl: kakaoCallbackUrl = null,
          platform: kakaoPlatform = null,
        } = kakaoAuthContext ? JSON.parse(kakaoAuthContext) : {};

        if (kakaoCallbackUrl === null || kakaoPlatform === null) {
          sessionStorage.removeItem('kakao_auth_context');
          if (!callbackUrl) {
            globalSpinnerController.hideSpinner();
          }
          return;
        }

        const updatedCallbackUrl = new URL(kakaoCallbackUrl);
        updatedCallbackUrl.searchParams.append('idToken', idToken);

        const isNeedOnboarding = await getIsNeedOnboarding({
          uid,
          token: idToken,
        });

        if (isNeedOnboarding) {
          updatedCallbackUrl.pathname = '/onboarding';
        }

        const redirectUrl = isNewUser
          ? `${
              PAGE_PATHS.SOCIAL_REGISTER_DONE
            }?callbackUrl=${encodeURIComponent(updatedCallbackUrl.toString())}`
          : linkedMethod
            ? `${PAGE_PATHS.CONNECT_DONE}?callbackUrl=${encodeURIComponent(
                updatedCallbackUrl.toString(),
              )}&linkedMethod=${linkedMethod}`
            : updatedCallbackUrl.toString();

        sessionStorage.removeItem('kakao_auth_context');
        window.location.href = redirectUrl;

        return;
      }

      if (callbackUrl) {
        const tokenParam = platform === 'web' ? 'idToken' : 'token';
        const tokenValue = platform === 'web' ? idToken : customToken || '';

        const updatedCallbackUrl = new URL(callbackUrl);
        updatedCallbackUrl.searchParams.append(tokenParam, tokenValue);

        const isNeedOnboarding = await getIsNeedOnboarding({
          uid,
          token: idToken,
        });

        if (isNeedOnboarding) {
          updatedCallbackUrl.pathname = '/onboarding';
        }

        const redirectUrl = isNewUser
          ? `${
              PAGE_PATHS.SOCIAL_REGISTER_DONE
            }?callbackUrl=${encodeURIComponent(updatedCallbackUrl.toString())}`
          : linkedMethod
            ? `${PAGE_PATHS.CONNECT_DONE}?callbackUrl=${encodeURIComponent(
                updatedCallbackUrl.toString(),
              )}&linkedMethod=${linkedMethod}`
            : updatedCallbackUrl.toString();

        window.location.href = redirectUrl;
      }
    } catch (error) {
      console.error(error);

      if (
        error instanceof FirebaseError &&
        error.code === AuthErrorCodes.NEED_CONFIRMATION
      ) {
        handleNeedConfirmationError(error);
      }
      captureException(error);
    }
  };

  const providerType = localStorage.getItem('providerType');
  if (providerType) {
    const ballon = document.querySelector(`.ballon-${providerType}`);
    if (isHTMLElement(ballon)) {
      ballon.style.visibility = 'visible';
    }
  }
} catch (e) {
  captureException(e);
}
